<template>
  <div class="fleet-list">
    <div class="d-flex justify-content-between align-items-center flex-wrap">
      <h6 v-if="!readonly && selected.length" class="mc-row-check mt-0">
        <span class="badge btn-success">{{ selected.length }}</span>
        {{ $t('global.object.selectedRow') }}
        <a class="color-primary" @click="clearDelete('addFleet')">{{
          $t('global.action.deselect')
        }}</a>
      </h6>

      <div class="d-flex justify-content-end flex-grow-1 gap-2 flex-wrap">
        <!-- Search Input -->
        <div id="tickets-table_filter" class="dataTables_filter text-md-end">
          <label
            class="d-inline-flex align-items-center mc-table-view__search"
            @click="focusSearch"
          >
            <i class="mc-fa fas fa-search"></i>
            <b-form-input
              v-model="filter"
              type="search"
              :placeholder="$t('global.search') + '...'"
              class="ms-2"
              ref="searchInput"
            />
          </label>
        </div>

        <!-- Delete Button -->
        <McButton
          v-if="!readonly && selected.length"
          v-b-modal.modal-delete-fleet
          variant="secondary-red"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
        />

        <!-- Add Button -->
        <McButton
          v-if="!readonly"
          v-b-modal.modal-add-fleet
          icon="icon-fill_plus"
          :text="$t('global.action.create')"
        />
      </div>
    </div>

    <!-- Table -->
    <div class="table-responsive my-3 border border-1">
      <b-table
        small
        show-empty
        :busy="isLoadingVehicles"
        :fields="displayFields"
        :items="linkedVehicle"
        class="datatables"
        responsive="md"
        sort-by="groupNameService"
        empty-table
        :empty-text="$t('global.emptyTable')"
        :per-page="perPage"
        :current-page="currentPage"
        :filter="filter"
        :filter-included-fields="filterOn"
        @filtered="onFiltered"
      >
        <template #table-busy>
          <div class="__busy">
            <b-spinner class="align-middle mb-2" />
            <strong>{{ $t('global.text.loading') }}...</strong>
          </div>
        </template>

        <template v-if="!readonly" #head(columnCheck)>
          <div class="form-check">
            <input
              v-model="allSelected"
              class="form-check-input me-1 custom-control-input"
              type="checkbox"
              value="all"
              @input="toggleAllAddVehicle"
            />
          </div>
        </template>

        <template v-if="!readonly" #cell(columnCheck)="{ item }">
          <div class="form-check">
            <input
              v-model="selected"
              class="form-check-input me-1 custom-control-input"
              type="checkbox"
              :value="item.id"
              @input="checkAddVehicle"
            />
          </div>
        </template>

        <template #cell(category)="{ item }">
          {{ item.vehicleType ? item.vehicleType.category : '-' }}
        </template>

        <template #cell(manufacturer)="{ item }">
          {{ item.vehicleType ? item.vehicleType.manufacturer : '-' }}
        </template>

        <template #cell(vehicleType)="{ item }">
          {{ item.vehicleType ? item.vehicleType.type : '-' }}
        </template>

        <template #cell(sequence)="{ index }">
          {{ (currentPage - 1) * perPage + index + 1 }}
        </template>
      </b-table>
    </div>

    <div class="row">
      <div class="col-sm-12 col-md-6">
        <div id="tickets-table_length" class="dataTables_length">
          <label class="d-inline-flex align-items-center">
            {{ $t('global.show') }}
            <b-form-select
              v-model="perPage"
              class="form-select form-select-sm"
              size="sm"
              :options="pageOptions"
            />
            {{ $t('global.entry') }}
          </label>
        </div>
      </div>
      <div class="col-sm-12 col-md-6">
        <div class="dataTables_paginate paging_simple_numbers float-end">
          <ul class="pagination pagination-rounded mb-0">
            <!-- pagination -->
            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
          </ul>
        </div>
      </div>
    </div>

    <!-- Modal for add fleet -->
    <ModalAddFleet
      v-if="!readonly"
      :linkedVehicle="linkedVehicle"
      @update:vehicleIds="updateVehicleIds"
    />

    <!-- Modal for Delete Confirmation -->
    <ModalDeleteFleet
      v-if="!readonly"
      :selectedVehicles="selectedVehicles"
      @deleteVehicleIds="deleteVehicleIds"
    />
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import ModalAddFleet from '../components/modals/ModalAddFleet'
import ModalDeleteFleet from '../components/modals/ModalDeleteFleet'

import { fields } from '../lib/fields/list-fleet-fields'

import { GET_VEHICLE } from '@/services/api/master/fleet'

export default {
  components: {
    McButton,
    ModalAddFleet,
    ModalDeleteFleet,
  },
  mixins: [fields],
  apollo: {
    vehicles: {
      query: GET_VEHICLE,
      variables() {
        return {
          filter: {
            orderBy: JSON.stringify({ licensePlate: 'asc' }),
            isHaveDevice: true,
          },
        }
      },
      result({ data }) {
        const self = this
        self.filteredItems = data.vehicles.vehicles
      },
    },
  },
  props: {
    vehicleIds: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showSequenceColumn: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:vehicleIds'],
  data() {
    return {
      filter: null,
      perPage: 10,
      currentPage: 1,

      filterOn: [],
      filteredItems: [],
      pageOptions: [10, 25, 50, 100],

      vehicles: {
        vehicles: [],
      },

      selected: [],
      selectedModel: [],
      allSelected: false,
    }
  },
  computed: {
    displayFields() {
      let fields = [...this.fields]

      if (this.showSequenceColumn) {
        fields = [
          {
            key: 'sequence',
            label: this.$t('trackingTemp.table.number'),
            sortable: false,
            thClass: 'mc-table--40',
          },
          ...fields,
        ]
      }

      if (this.readonly) {
        return fields.filter((field) => field.key !== 'columnCheck')
      }

      return fields
    },

    isLoadingVehicles() {
      return this.$apollo?.queries?.vehicles?.loading ?? false
    },

    totalRows() {
      return this.linkedVehicle.length
    },

    linkedVehicle() {
      let vehicleIds = this.vehicleIds

      const vehicles = []
      this.vehicles.vehicles.forEach(function (item) {
        if (vehicleIds.includes(item.id)) {
          vehicles.push(item)
        }
      })

      return vehicles
    },

    selectedVehicles() {
      let vehicleIds = this.selected

      let vehicles = []
      this.vehicles.vehicles.forEach(function (item) {
        if (vehicleIds.includes(item.id)) {
          vehicles.push(item)
        }
      })

      return vehicles
    },
  },
  methods: {
    /**
     * Search the table data with search input
     */

    focusSearch() {
      this.$refs.searchInput.focus()
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.filteredItems = filteredItems
      this.currentPage = 1
    },

    toggleAllAddVehicle(event) {
      let checked = event.target.checked
      this.selected = []

      if (checked) {
        this.selected = this.linkedVehicle.map((item) => item.id)
      } else {
        this.selected = []
      }
    },

    checkAddVehicle(event) {
      if (event.target.checked) {
        this.selected.push(parseInt(event.target.value))
        if (this.selected.length == this.linkedVehicle.length) {
          this.allSelected = true
        }
      } else {
        this.allSelected = false
        let index = this.selected.indexOf(parseInt(event.target.value))
        if (index !== -1) {
          this.selected.splice(index, 1)
        }
      }
    },

    clearDelete() {
      this.selected = []
      this.allSelected = false
    },

    deleteVehicleIds() {
      let newVehicleIds = [...this.vehicleIds]

      this.selected.forEach(function (item) {
        let index = newVehicleIds.indexOf(parseInt(item))

        if (index !== -1) {
          newVehicleIds.splice(index, 1)
        }
      })

      this.$emit('update:vehicleIds', newVehicleIds)

      this.selected = []
      this.allSelected = false

      this.$bvModal.hide('modal-delete-fleet')
    },

    updateVehicleIds(values) {
      let newVehicleIds = [...this.vehicleIds, ...values]

      this.$emit('update:vehicleIds', newVehicleIds)
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-has-search {
  border-width: $input-border-width;
  border-color: $input-border-color;
  border-radius: 4px;
  border-style: solid;
  margin-left: 0;

  border-radius: 4px;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    height: 37px;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon:before {
      font-size: 16px;
      color: $input-placeholder-color;
    }
  }
}

.fleet-list {
  :deep(.datatables) {
    .__busy {
      min-height: 180px;
      display: grid;
      place-items: center;
      place-content: center;
      text-align: center;
    }
  }
}
</style>
